import * as tf from '@tensorflow/tfjs';
import type { Dataset, VAEConfig } from './types.ts';
import { VAEModel } from './vae/model.ts';
import { loadDataset } from './vae/data.ts';
import { MorphController } from './viz/morph.ts';


interface AppState {
  model: VAEModel | null;
  dataset: Dataset | null;
  isTraining: boolean;
  currentEpoch: number;
  totalEpochs: number;
  currentLatent: Float32Array;
  latentPoints: Array<{ z: Float32Array; label: number; x: number; y: number }>;
}

const state: AppState = {
  model: null,
  dataset: null,
  isTraining: false,
  currentEpoch: 0,
  totalEpochs: 10,
  currentLatent: new Float32Array(2),
  latentPoints: [],
};

let morphController: MorphController | null = null;

function getElement<T extends HTMLElement>(id: string): T {
  const el = document.getElementById(id);
  if (!el) throw new Error(`Element #${id} not found`);
  return el as T;
}

async function init(): Promise<void> {
  const savedBackend = localStorage.getItem('vae-backend') || 'webgl';
  try {
    await tf.setBackend(savedBackend);
    await tf.ready();
  } catch {
    await tf.setBackend('cpu');
    await tf.ready();
  }

  const backendSelect = getElement<HTMLSelectElement>('backend-select');
  backendSelect.value = tf.getBackend() as string;
  backendSelect.addEventListener('change', async () => {
    const newBackend = backendSelect.value;
    localStorage.setItem('vae-backend', newBackend);
    await tf.setBackend(newBackend);
    await tf.ready();
    getElement('status-text').textContent = `Switched to ${newBackend.toUpperCase()} backend`;
  });

  console.log('TensorFlow.js backend:', tf.getBackend());

  setupEventListeners();
  await loadInitialDataset();
  createInitialModel();
  setupMorphController();
}

function setupEventListeners(): void {
  const modeSelect = getElement<HTMLSelectElement>('mode-select');
  const datasetSelect = getElement<HTMLSelectElement>('dataset-select');
  const latentDim = getElement<HTMLInputElement>('latent-dim');
  const hiddenUnits = getElement<HTMLInputElement>('hidden-units');
  const learningRate = getElement<HTMLInputElement>('learning-rate');
  const klWeight = getElement<HTMLInputElement>('kl-weight');
  const epochs = getElement<HTMLInputElement>('epochs');

  modeSelect.addEventListener('change', () => {
    const simpleOnly = document.querySelectorAll('.simple-only');
    simpleOnly.forEach(el => {
      el.classList.toggle('hidden', modeSelect.value !== 'simple');
    });
    createInitialModel();
  });

  datasetSelect.addEventListener('change', handleDatasetChange);

  latentDim.addEventListener('input', () => {
    getElement('latent-dim-value').textContent = latentDim.value;
    updateLatentBadge(parseInt(latentDim.value));
    createInitialModel();
  });

  hiddenUnits.addEventListener('input', () => {
    getElement('hidden-units-value').textContent = hiddenUnits.value;
  });

  learningRate.addEventListener('input', () => {
    getElement('learning-rate-value').textContent = parseFloat(learningRate.value).toFixed(4);
  });

  klWeight.addEventListener('input', () => {
    getElement('kl-weight-value').textContent = parseFloat(klWeight.value).toFixed(1);
  });

  epochs.addEventListener('change', () => {
    state.totalEpochs = parseInt(epochs.value);
  });

  getElement<HTMLButtonElement>('train-btn').addEventListener('click', startTraining);
  getElement<HTMLButtonElement>('pause-btn').addEventListener('click', pauseTraining);
  getElement<HTMLButtonElement>('reset-btn').addEventListener('click', resetModel);
  getElement<HTMLButtonElement>('load-pretrained-btn').addEventListener('click', loadPretrained);
  getElement<HTMLButtonElement>('encode-dataset-btn').addEventListener('click', encodeDataset);
  getElement<HTMLButtonElement>('clear-latent-btn').addEventListener('click', clearLatentSpace);
  getElement<HTMLButtonElement>('random-sample-btn').addEventListener('click', randomSample);
  getElement<HTMLButtonElement>('reset-sliders-btn').addEventListener('click', resetSliders);
  getElement<HTMLButtonElement>('set-morph-a').addEventListener('click', setMorphA);
  getElement<HTMLButtonElement>('set-morph-b').addEventListener('click', setMorphB);
  getElement<HTMLButtonElement>('play-morph-btn').addEventListener('click', playMorph);
  getElement<HTMLButtonElement>('stop-morph-btn').addEventListener('click', stopMorph);
  getElement<HTMLButtonElement>('refresh-grid-btn').addEventListener('click', refreshReconstructionGrid);

  getElement<HTMLInputElement>('morph-steps').addEventListener('input', (e) => {
    getElement('morph-steps-value').textContent = (e.target as HTMLInputElement).value;
  });

  getElement<HTMLInputElement>('morph-speed').addEventListener('input', (e) => {
    getElement('morph-speed-value').textContent = (e.target as HTMLInputElement).value + 'ms';
  });

  const latentCanvas = getElement<HTMLCanvasElement>('latent-canvas');
  latentCanvas.addEventListener('click', handleLatentCanvasClick);
}

function updateLatentBadge(dim: number): void {
  const badge = document.querySelector('.latent-dim-badge');
  if (badge) {
    badge.textContent = dim + 'D';
  }
  const hint2d = document.querySelector('.hint-2d');
  const hintHighDim = document.querySelector('.hint-high-dim');
  if (hint2d && hintHighDim) {
    hint2d.classList.toggle('hidden', dim !== 2);
    hintHighDim.classList.toggle('hidden', dim === 2);
  }
}

async function loadInitialDataset(): Promise<void> {
  const status = getElement('status-text');
  status.textContent = 'Loading dataset...';
  try {
    state.dataset = await loadDataset('mnist-small');
    status.textContent = `Loaded ${state.dataset.name}: ${state.dataset.train.length} training samples`;
  } catch (err) {
    status.textContent = 'Failed to load dataset: ' + (err as Error).message;
  }
}

async function handleDatasetChange(): Promise<void> {
  const select = getElement<HTMLSelectElement>('dataset-select');
  const status = getElement('status-text');
  status.textContent = 'Loading dataset...';
  try {
    state.dataset = await loadDataset(select.value);
    status.textContent = `Loaded ${state.dataset.name}: ${state.dataset.train.length} training samples`;
    state.latentPoints = [];
    drawLatentSpace();
  } catch (err) {
    status.textContent = 'Failed to load dataset: ' + (err as Error).message;
  }
}

function createInitialModel(): void {
  if (state.model) {
    state.model.dispose();
  }

  const config: VAEConfig = {
    mode: getElement<HTMLSelectElement>('mode-select').value as 'simple' | 'modern',
    latentDim: parseInt(getElement<HTMLInputElement>('latent-dim').value),
    simpleModeHiddenUnits: parseInt(getElement<HTMLInputElement>('hidden-units').value),
    learningRate: parseFloat(getElement<HTMLInputElement>('learning-rate').value),
    batchSize: parseInt(getElement<HTMLSelectElement>('batch-size').value),
    epochs: parseInt(getElement<HTMLInputElement>('epochs').value),
    betaVaeWeight: parseFloat(getElement<HTMLInputElement>('kl-weight').value),
  };

  state.model = new VAEModel(config);
  state.currentLatent = new Float32Array(config.latentDim);
  updateLatentSliders(config.latentDim);
}

function setupMorphController(): void {
  const canvas = getElement<HTMLCanvasElement>('morph-output-canvas');
  morphController = new MorphController(canvas, async (z) => {
    if (!state.model) throw new Error('No model');
    const zTensor = tf.tensor2d([Array.from(z)]);
    const recon = state.model.decode(zTensor);
    const data = await recon.data();
    zTensor.dispose();
    recon.dispose();
    return new Float32Array(data);
  });
}

async function startTraining(): Promise<void> {
  if (!state.model || !state.dataset) return;

  state.isTraining = true;
  state.currentEpoch = 0;
  const epochs = parseInt(getElement<HTMLInputElement>('epochs').value);
  state.totalEpochs = epochs;

  getElement<HTMLButtonElement>('train-btn').disabled = true;
  getElement<HTMLButtonElement>('pause-btn').disabled = false;

  const batchSize = parseInt(getElement<HTMLSelectElement>('batch-size').value);
  const data = state.dataset.train;
  const numBatches = Math.ceil(data.length / batchSize);

    for (let epoch = 0; epoch < epochs && state.isTraining; epoch++) {
      state.currentEpoch = epoch;
      updateEpochDisplay(epoch, epochs);

      let epochLoss = 0;
      let epochRecon = 0;
      let epochKl = 0;

      for (let batchIdx = 0; batchIdx < numBatches && state.isTraining; batchIdx++) {
        tf.engine().startScope();
        const batch = data.slice(batchIdx * batchSize, (batchIdx + 1) * batchSize);
        const images = batch.map(s => s.image);
        const flatImages = new Float32Array(images.length * 28 * 28);
        for (let i = 0; i < images.length; i++) {
          flatImages.set(images[i], i * 28 * 28);
        }
        const imageTensor = tf.tensor4d(flatImages, [images.length, 28, 28, 1]);

        const { loss, reconLoss, klLoss } = await state.model.trainStep(imageTensor);
        imageTensor.dispose();

        epochLoss += loss;
        epochRecon += reconLoss;
        epochKl += klLoss;

        const progress = ((epoch * numBatches + batchIdx) / (epochs * numBatches)) * 100;
        updateProgress(progress, loss, reconLoss, klLoss);
        tf.engine().endScope();

        await new Promise(r => setTimeout(r, 5));
      }

      if (state.isTraining) {
        epochLoss /= numBatches;
        epochRecon /= numBatches;
        epochKl /= numBatches;
        console.log(`Epoch ${epoch + 1}/${epochs} - Loss: ${epochLoss.toFixed(4)}, Recon: ${epochRecon.toFixed(4)}, KL: ${epochKl.toFixed(4)}`);
      }
    }

  state.isTraining = false;
  getElement<HTMLButtonElement>('train-btn').disabled = false;
  getElement<HTMLButtonElement>('pause-btn').disabled = true;
  getElement('status-text').textContent = 'Training complete';

  try {
    await state.model.save();
    console.log('Model saved');
  } catch (err) {
    console.error('Failed to save model:', err);
  }
}

function pauseTraining(): void {
  state.isTraining = false;
  getElement<HTMLButtonElement>('train-btn').disabled = false;
  getElement<HTMLButtonElement>('pause-btn').disabled = true;
  getElement('status-text').textContent = 'Training paused';
}

function resetModel(): void {
  state.isTraining = false;
  createInitialModel();
  getElement('status-text').textContent = 'Model reset';
  updateProgress(0, 0, 0, 0);
}

async function loadPretrained(): Promise<void> {
  if (!state.model) return;
  try {
    await state.model.load();
    getElement('status-text').textContent = 'Loaded pre-trained model';
  } catch {
    getElement('status-text').textContent = 'No pre-trained model found';
  }
}

function updateEpochDisplay(current: number, total: number): void {
  getElement('metric-epoch').textContent = `${current + 1}/${total}`;
}

function updateProgress(percent: number, loss: number, recon: number, kl: number): void {
  getElement<HTMLDivElement>('progress-fill').style.width = percent + '%';
  getElement('metric-loss').textContent = loss.toFixed(3);
  getElement('metric-recon').textContent = recon.toFixed(3);
  getElement('metric-kl').textContent = kl.toFixed(3);
}

async function encodeDataset(): Promise<void> {
  if (!state.model || !state.dataset) return;

  const samples = state.dataset.test.slice(0, 1000);
  const images = samples.map(s => s.image);
  const flatImages = new Float32Array(images.length * 28 * 28);
  for (let i = 0; i < images.length; i++) {
    flatImages.set(images[i], i * 28 * 28);
  }
  const imageTensor = tf.tensor4d(flatImages, [images.length, 28, 28, 1]);

  const [mu] = state.model.encode(imageTensor);
  const muData = await mu.data();
  mu.dispose();
  imageTensor.dispose();

  state.latentPoints = [];
  const latentDim = state.model.config.latentDim;

  for (let i = 0; i < samples.length; i++) {
    const z = new Float32Array(latentDim);
    for (let j = 0; j < latentDim; j++) {
      z[j] = muData[i * latentDim + j];
    }
    state.latentPoints.push({
      z,
      label: samples[i].label,
      x: latentDim >= 2 ? z[0] : 0,
      y: latentDim >= 2 ? z[1] : 0,
    });
  }

  drawLatentSpace();
}

function clearLatentSpace(): void {
  state.latentPoints = [];
  drawLatentSpace();
}

function drawLatentSpace(): void {
  const canvas = getElement<HTMLCanvasElement>('latent-canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  ctx.fillStyle = 'rgba(0, 0, 0, 0.25)';
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  if (state.latentPoints.length === 0) return;

  const colors = [
    '#ef4444', '#f97316', '#f59e0b', '#84cc16', '#22c55e',
    '#10b981', '#06b6d4', '#3b82f6', '#8b5cf6', '#d946ef',
  ];

  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
  for (const p of state.latentPoints) {
    minX = Math.min(minX, p.x);
    maxX = Math.max(maxX, p.x);
    minY = Math.min(minY, p.y);
    maxY = Math.max(maxY, p.y);
  }

  const padding = 40;
  const scaleX = (canvas.width - padding * 2) / (maxX - minX || 1);
  const scaleY = (canvas.height - padding * 2) / (maxY - minY || 1);

  for (const p of state.latentPoints) {
    const x = padding + (p.x - minX) * scaleX;
    const y = canvas.height - (padding + (p.y - minY) * scaleY);

    ctx.beginPath();
    ctx.arc(x, y, 3, 0, Math.PI * 2);
    ctx.fillStyle = colors[p.label % colors.length];
    ctx.fill();
  }
}

function handleLatentCanvasClick(e: MouseEvent): void {
  if (!state.model || state.model.config.latentDim !== 2) return;

  const canvas = getElement<HTMLCanvasElement>('latent-canvas');
  const rect = canvas.getBoundingClientRect();
  const x = (e.clientX - rect.left) * (canvas.width / rect.width);
  const y = (e.clientY - rect.top) * (canvas.height / rect.height);

  if (state.latentPoints.length > 0) {
    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
    for (const p of state.latentPoints) {
      minX = Math.min(minX, p.x);
      maxX = Math.max(maxX, p.x);
      minY = Math.min(minY, p.y);
      maxY = Math.max(maxY, p.y);
    }
    const padding = 40;
    const scaleX = (canvas.width - padding * 2) / (maxX - minX || 1);
    const scaleY = (canvas.height - padding * 2) / (maxY - minY || 1);

    const z0 = minX + (x - padding) / scaleX;
    const z1 = minY + (canvas.height - y - padding) / scaleY;

    state.currentLatent[0] = z0;
    state.currentLatent[1] = z1;
  } else {
    state.currentLatent[0] = (x / canvas.width - 0.5) * 6;
    state.currentLatent[1] = ((canvas.height - y) / canvas.height - 0.5) * 6;
  }

  updateSlidersFromLatent();
  reconstructCurrent();
}

function updateLatentSliders(dim: number): void {
  const container = getElement('latent-sliders');
  container.innerHTML = '';

  for (let i = 0; i < dim; i++) {
    const row = document.createElement('div');
    row.className = 'slider-row';

    const label = document.createElement('label');
    label.textContent = `z[${i}]`;

    const slider = document.createElement('input');
    slider.type = 'range';
    slider.min = '-3';
    slider.max = '3';
    slider.step = '0.1';
    slider.value = '0';
    slider.dataset.index = String(i);
    slider.addEventListener('input', handleSliderChange);

    row.appendChild(label);
    row.appendChild(slider);
    container.appendChild(row);
  }
}

function handleSliderChange(e: Event): void {
  const slider = e.target as HTMLInputElement;
  const index = parseInt(slider.dataset.index || '0');
  const value = parseFloat(slider.value);

  if (state.currentLatent.length > index) {
    state.currentLatent[index] = value;
    reconstructCurrent();
  }
}

function updateSlidersFromLatent(): void {
  const sliders = document.querySelectorAll('.slider-row input[type="range"]');
  sliders.forEach((slider, i) => {
    if (i < state.currentLatent.length) {
      (slider as HTMLInputElement).value = String(state.currentLatent[i]);
    }
  });
}

async function reconstructCurrent(): Promise<void> {
  if (!state.model) return;

  const zTensor = tf.tensor2d([Array.from(state.currentLatent)]);
  const recon = state.model.decode(zTensor);
  const data = await recon.data();
  zTensor.dispose();
  recon.dispose();

  drawImageToCanvas(getElement<HTMLCanvasElement>('output-canvas'), new Float32Array(data));
}

function drawImageToCanvas(canvas: HTMLCanvasElement, image: Float32Array): void {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  const size = 28;
  const imgData = ctx.createImageData(size, size);
  for (let i = 0; i < size * size; i++) {
    const val = Math.max(0, Math.min(255, Math.round(image[i] * 255)));
    imgData.data[i * 4] = val;
    imgData.data[i * 4 + 1] = val;
    imgData.data[i * 4 + 2] = val;
    imgData.data[i * 4 + 3] = 255;
  }
  ctx.imageSmoothingEnabled = false;
  ctx.putImageData(imgData, 0, 0);
}

function randomSample(): void {
  const dim = state.model?.config.latentDim || 2;
  for (let i = 0; i < dim; i++) {
    state.currentLatent[i] = (Math.random() - 0.5) * 4;
  }
  updateSlidersFromLatent();
  reconstructCurrent();
}

function resetSliders(): void {
  state.currentLatent.fill(0);
  updateSlidersFromLatent();
  reconstructCurrent();
}

async function setMorphA(): Promise<void> {
  if (!morphController) return;
  morphController.setFrom(state.currentLatent);
  await drawMorphPreview('morph-a-preview', state.currentLatent);
}

async function setMorphB(): Promise<void> {
  if (!morphController) return;
  morphController.setTo(state.currentLatent);
  await drawMorphPreview('morph-b-preview', state.currentLatent);
}

async function drawMorphPreview(canvasId: string, z: Float32Array): Promise<void> {
  if (!state.model) return;
  const zTensor = tf.tensor2d([Array.from(z)]);
  const recon = state.model.decode(zTensor);
  const data = await recon.data();
  zTensor.dispose();
  recon.dispose();
  drawImageToCanvas(getElement<HTMLCanvasElement>(canvasId), new Float32Array(data));
}

function playMorph(): void {
  if (!morphController) return;
  const from = morphController.getFrom();
  const to = morphController.getTo();
  if (!from || !to) {
    alert('Please set both morph points A and B');
    return;
  }

  const steps = parseInt(getElement<HTMLInputElement>('morph-steps').value);
  const speed = parseInt(getElement<HTMLInputElement>('morph-speed').value);
  const loop = getElement<HTMLInputElement>('morph-loop').checked;
  const interpolation = getElement<HTMLSelectElement>('morph-interpolation').value as 'linear' | 'spherical';

  morphController.play({
    from,
    to,
    steps,
    interpolation,
    frameDurationMs: speed,
    loop,
  });
}

function stopMorph(): void {
  morphController?.stop();
}

async function refreshReconstructionGrid(): Promise<void> {
  if (!state.model || !state.dataset) return;

  const grid = getElement('reconstruction-grid');
  grid.innerHTML = '';

  const samples = state.dataset.test.slice(0, 20);
  const showOriginals = getElement<HTMLInputElement>('show-originals').checked;

  for (const sample of samples) {
    const item = document.createElement('div');
    item.className = 'recon-item';

    const pair = document.createElement('div');
    pair.className = 'recon-pair';

    if (showOriginals) {
      const origCanvas = document.createElement('canvas');
      origCanvas.width = 28;
      origCanvas.height = 28;
      drawImageToCanvas(origCanvas, sample.image);
      pair.appendChild(origCanvas);
    }

    const reconCanvas = document.createElement('canvas');
    reconCanvas.width = 28;
    reconCanvas.height = 28;

    const imgTensor = tf.tensor4d(sample.image, [1, 28, 28, 1]);
    const recon = state.model.decode(state.model.sample(...state.model.encode(imgTensor)));
    const reconData = await recon.data();
    imgTensor.dispose();
    recon.dispose();

    drawImageToCanvas(reconCanvas, new Float32Array(reconData));
    pair.appendChild(reconCanvas);

    const label = document.createElement('div');
    label.className = 'recon-label';
    label.textContent = state.dataset.classNames[sample.label];

    item.appendChild(pair);
    item.appendChild(label);
    grid.appendChild(item);
  }
}

init().catch(console.error);
